import { useLocation } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { useMeQuery } from '@platform/state/api';
import { activeGroupKey, modeGroups, useNavMode } from '@platform/navigation';
import type { IShellNav } from './IShellNav';
import { useWorkspaceCatalog } from './useWorkspaceCatalog';

/**
 * Глобальна навігація поточної сторінки: режим навігації користувача (useNavMode — окремо для кожного користувача), групи режиму,
 * активна група — група відкритого модуля (activeGroupKey) або «Головна» на Головній робочого простору, її поданнях «Моя робота» та на
 * сторінках системних модулів «Адміністрування» й «Налаштування» (як у прототипі v4). Каталог — за правилом реєстрів Ради (useWorkspaceCatalog).
 * Тип записів — з маршруту, а на сторінках налаштувань дочірньої таблиці — з `?type=` (у прототипі це окремий модуль, пігулка «Офіс · Видатки»).
 */
export function useShellNav(ws: string): IShellNav {
  const route = useWorkspaceParams();
  const module = route.module || undefined;
  const routeType = route.rt || undefined;
  const { pathname, search } = useLocation();
  // налаштування дочірньої таблиці (/<група>/<модуль>/settings/:item?type=<тип>) — як окремий модуль прототипу: пігулка «Офіс · Видатки», як на її списку
  const settings = module ? routes.moduleSettings(module) : '';
  const onSettings = !!module && (pathname === settings || pathname.startsWith(`${settings}/`));
  const rt = routeType ?? (onSettings ? new URLSearchParams(search).get('type') ?? undefined : undefined);
  const { data: me } = useMeQuery();
  const { catalog, parliament } = useWorkspaceCatalog(ws);
  const [mode, setMode] = useNavMode(me?.id);
  const groups = modeGroups(mode);
  const onHome = !module && /^(\/work\/[^/]+)?\/?$/.test(pathname);
  const home = groups.find((g) => g.home)?.key ?? null;
  const area = !module ? decodeURIComponent(pathname.split('/')[1] ?? '') : '';
  const areaGroup = area && groups.some((g) => g.key === area) ? area : null;
  const sys = !module ? /^\/(admin|customize)(\/|$)/.exec(pathname)?.[1] : undefined;
  const system = sys === 'admin' || sys === 'customize' ? sys : null;
  const activeGroup = module ? activeGroupKey(mode, module, rt) : onHome || system ? home : areaGroup;
  return { me, catalog, parliament, mode, setMode, groups, activeGroup, module, recordType: rt, onHome, onArea: !!areaGroup, system };
}
